import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { apifyBrandInfoExtractor } from '@/domains/brands/infrastructure/apifyBrandInfoExtractor';
import { useOrganization } from './useOrganization';
import { useOrganizationLogo } from './useOrganizationLogo';
import type { BrandSearchCandidate } from '@/domains/brands/types/brand.types';
import { useToast } from '@/hooks/use-toast';

export interface FetchedLogoResult {
  domain: string;
  name: string;
  logoUrl: string | null;
  websiteUrl: string;
}

export function useFetchOrganizationLogo() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { applyFetchedLogo, isApplyingFetchedLogo } = useOrganizationLogo();
  const { toast } = useToast();

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [candidates, setCandidates] = useState<BrandSearchCandidate[] | null>(null);
  const [fetchedResult, setFetchedResult] = useState<FetchedLogoResult | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchLogo = useCallback(
    async (input: string) => {
      const trimmed = input.trim();
      if (!trimmed) {
        setFetchError('Enter a company name or website URL.');
        return;
      }
      if (!orgId) {
        setFetchError('No active organization selected.');
        return;
      }

      setIsFetching(true);
      setFetchError(null);
      setCandidates(null);
      setFetchedResult(null);

      try {
        const res = await apifyBrandInfoExtractor.extractBrandInfo(orgId, trimmed);

        if (res.status === 'ok') {
          setFetchedResult({
            domain: res.data.domain,
            name: res.data.brandName,
            logoUrl: res.data.logoUrl,
            websiteUrl: res.data.websiteUrl,
          });
        } else if (res.status === 'candidates') {
          setCandidates(res.results);
        } else if (res.status === 'duplicate') {
          // If a brand with this domain exists, retrieve its logo
          const { data: brand } = await supabase
            .from('brands')
            .select('id, name, logo_url, website_url')
            .eq('id', res.brandId)
            .maybeSingle();

          let logoUrl = brand?.logo_url || null;

          if (!logoUrl) {
            const { data: meta } = await supabase
              .from('domain_metadata' as 'brands')
              .select('logo_url' as 'name')
              .eq('domain' as 'id', res.domain)
              .maybeSingle();
            logoUrl = (meta as unknown as { logo_url?: string | null })?.logo_url || null;
          }

          setFetchedResult({
            domain: res.domain,
            name: brand?.name || res.brandName,
            logoUrl,
            websiteUrl: brand?.website_url || `https://${res.domain}`,
          });
        }
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : 'Could not fetch organization logo. Please try again.';
        setFetchError(msg);
        toast({
          title: 'Fetch Failed',
          description: msg,
          variant: 'destructive',
        });
      } finally {
        setIsFetching(false);
      }
    },
    [orgId, toast]
  );

  const selectCandidate = useCallback(
    async (domain: string) => {
      await fetchLogo(`https://${domain}`);
    },
    [fetchLogo]
  );

  const reset = useCallback(() => {
    setIsFetching(false);
    setCandidates(null);
    setFetchedResult(null);
    setFetchError(null);
  }, []);

  const applyLogo = useCallback(
    async (url: string) => {
      return applyFetchedLogo(url);
    },
    [applyFetchedLogo]
  );

  return {
    fetchLogo,
    selectCandidate,
    reset,
    applyLogo,
    isFetching,
    isApplying: isApplyingFetchedLogo,
    candidates,
    fetchedResult,
    fetchError,
  };
}
